import { Button, Layout, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineDownload, AiOutlineMenu } from "react-icons/ai";

import { DataTable } from "design-system/components/DataTable";
import {
  useAuditLogsLazyQuery,
  useExportAuditLogsMutation,
} from "generated/graphql";
import { formatDateToCustomFormat } from "shell/platform/utils/dateFormatter";

import type { FiltersProps } from "./type";

import s from "./AuditLog.module.css";
import { exportAuditLogCsv } from "./exportAuditLogCsv";
import { Filters } from "./Filters";

const { Header, Content } = Layout;
const { Text } = Typography;

const initialFilters = {
  name: null,
  email: null,
  section: null,
  region: null,
  fromDate: dayjs().subtract(30, "day"),
  toDate: dayjs().endOf("day"),
};

export const AuditLog = () => {
  const [filters, setFilters] = useState<FiltersProps>(initialFilters);
  const { t } = useTranslation(["common"]);
  const [fetchAuditLogs, { data, loading }] = useAuditLogsLazyQuery();
  const [exportAuditLogs, { loading: exportLoading }] =
    useExportAuditLogsMutation();
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [title, setTitle] = useState<string>(
    t("components.auditLog.recentActivityLog"),
  );

  const handleSearch = (cursor: string | null = null) => {
    fetchAuditLogs({
      variables: {
        startTime: filters.fromDate?.toISOString(),
        endTime: filters.toDate?.toISOString(),
        ...(filters.name && { user: filters.name }),
        ...(filters.email && { email: filters.email }),
        ...(filters.section && { section: filters.section }),
        ...(filters.region && { region: filters.region }),
        lastEvaluatedKey: cursor,
        limit: 10,
      },
    });
  };

  useEffect(() => {
    handleSearch();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      title: t("common:labels.date"),
      dataIndex: "timestamp",
      render: (timestamp: string) => formatDateToCustomFormat(timestamp),
    },
    {
      title: t("common:labels.nameLabel"),
      dataIndex: "user",
    },
    {
      title: t("common:labels.email"),
      dataIndex: "email",
    },
    {
      title: t("components.auditLog.section"),
      dataIndex: "section",
    },
    {
      title: t("labels.region"),
      dataIndex: "region",
    },
    {
      title: t("common:labels.details"),
      dataIndex: "details",
    },
  ];

  const resetFilters = () => {
    const { fromDate, toDate, name, email, section, region } = initialFilters;
    setFilters(initialFilters);
    fetchAuditLogs({
      variables: {
        startTime: fromDate?.toISOString(),
        endTime: toDate?.toISOString(),
        user: name,
        email,
        section,
        region,
        lastEvaluatedKey: null,
        limit: 10,
      },
    });
    setFilterResetKey(prev => prev + 1);
    setTitle(t("components.auditLog.recentActivityLog"));
  };

  const handleFullExport = async () => {
    const result = await exportAuditLogs({
      variables: {
        input: {
          startTime: filters.fromDate?.toISOString(),
          endTime: filters.toDate?.toISOString(),
          ...(filters.name && { user: filters.name }),
          ...(filters.email && { email: filters.email }),
          ...(filters.section && { section: filters.section }),
          ...(filters.region && { region: filters.region }),
        },
      },
    });
    const logs = result?.data?.exportAuditLogs?.logs ?? [];
    const dateStr = new Date().toISOString().split("T")[0];
    exportAuditLogCsv(logs, `audit-log-full-${dateStr}`);
  };

  const handleExport = () => {
    const dateStr = new Date().toISOString().split("T")[0];
    exportAuditLogCsv(data?.auditLogs?.logs ?? [], `audit-log-${dateStr}`);
  };

  const dataLength = data?.auditLogs?.logs.length || 0;

  return (
    <Layout>
      <Header className={s.header}>
        <Typography.Title level={3}>
          {t("components.auditLog.title")}
        </Typography.Title>
      </Header>
      <Content className={s.content}>
        <Filters
          filters={filters}
          onFiltersChange={setFilters}
          resetFilters={resetFilters}
          handleSearch={() => {
            handleSearch();
            setFilterResetKey(prev => prev + 1);
            setTitle(t("components.auditLog.showResults"));
          }}
        />
        <DataTable
          title={<Text strong>{title}</Text>}
          key={filterResetKey}
          data={data?.auditLogs?.logs ?? []}
          columns={columns}
          showHeader={!!dataLength}
          loading={loading}
          rightActions={[
            <Button
              type="default"
              icon={<AiOutlineDownload />}
              onClick={handleExport}
              data-testid="export-button"
              key="export-button"
              disabled={!dataLength}
            >
              {t("components.auditLog.export")}
            </Button>,
            <Button
              type="default"
              icon={<AiOutlineDownload />}
              onClick={handleFullExport}
              data-testid="export-all-button"
              key="export-all-button"
              loading={exportLoading}
              disabled={exportLoading}
            >
              {t("components.auditLog.exportAll")}
            </Button>,
          ]}
          pagination={
            dataLength
              ? {
                  lastEvaluatedKey: data?.auditLogs?.lastEvaluatedKey,
                  onNext: (cursor: string | null) => {
                    handleSearch(cursor);
                  },
                  onPrev: (cursor: string | null) => {
                    handleSearch(cursor);
                  },
                  resetPaginationKey: filterResetKey,
                }
              : false
          }
          customizableColumns={{
            tableName: "auditLogTable",
            callback: resetFilters,
            icon: <AiOutlineMenu />,
            disabled: !dataLength,
          }}
        />
      </Content>
    </Layout>
  );
};
